import { useCallback, useEffect, useState } from 'react';
import {
  listMyNotifications as customerList,
  type InboxNotification,
} from '../../lib/customer-api';
import { listMyNotifications as proList } from '../../lib/professional-api';
import {
  deleteNotification,
  markAllNotificationsRead,
  markNotificationRead,
  useUnreadNotifications,
} from '../../lib/use-notifications';
import { ConfirmModal } from './ConfirmModal';
import { Pagination } from './Pagination';
import { toast } from '../../lib/toast';

type Role = 'customer' | 'professional';

type Props = {
  role: Role;
};

const PAGE_SIZE = 10;

const formatWhen = (iso: string | null) => {
  if (!iso) return '';
  try {
    const date = new Date(iso);
    const diffMs = Date.now() - date.getTime();
    const min = Math.floor(diffMs / 60_000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const days = Math.floor(hr / 24);
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '';
  }
};

/**
 * Reusable inbox list rendered inside the role-specific page wrappers.
 * Loads the first page on mount, refreshes the unread badge after a
 * mark-as-read, and supports a "Mark all as read" bulk action.
 *
 * Clicking a row marks it read in the DB then expands the body inline.
 * Tapping a notification with an `action` payload still navigates —
 * but for now we just surface the body, since deep-link routing
 * differs between customer and pro contexts and isn't part of this
 * round.
 */
export function InboxList({ role }: Props) {
  const fetcher = role === 'customer' ? customerList : proList;
  const { refresh } = useUnreadNotifications(role);

  const [items, setItems] = useState<InboxNotification[]>([]);
  const [total, setTotal] = useState(0);
  // Global unread across all pages (from the API), not just this page —
  // so the header count, the "Mark all" button and its confirm message
  // stay accurate while paginating.
  const [totalUnread, setTotalUnread] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // "Mark all as read" is destructive-ish (clears every badge), so it
  // runs behind a confirmation popup.
  const [confirmAllOpen, setConfirmAllOpen] = useState(false);
  // Per-row delete confirmation. Stores the id (and whether it was
  // unread) so we can show the right title + update the unread count.
  const [confirmDelete, setConfirmDelete] = useState<
    { id: string; unread: boolean } | null
  >(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetcher({
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      });
      setItems(res.data ?? []);
      setTotal(res.total_count ?? 0);
      setTotalUnread(res.unread_count ?? 0);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ??
          err?.message ??
          'Could not load notifications.',
      );
    } finally {
      setLoading(false);
    }
  }, [fetcher, page]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRowClick = async (n: InboxNotification) => {
    // Expand / collapse the body so the user can read the full text
    // without leaving the page. First tap also marks it read.
    setExpandedId((prev) => (prev === n.id ? null : n.id));
    if (n.read_at) return;

    setBusyId(n.id);
    // Optimistic: flip read state locally so the badge animates away
    // without waiting for the network. If the call fails we restore
    // the unread state.
    setItems((prev) =>
      prev.map((row) =>
        row.id === n.id
          ? {
              ...row,
              status: 'READ',
              read_at: new Date().toISOString(),
            }
          : row,
      ),
    );
    setTotalUnread((n2) => Math.max(0, n2 - 1));
    try {
      await markNotificationRead(role, n.id);
      // Refresh the header badge so the count in the bell drops.
      refresh();
    } catch {
      setItems((prev) =>
        prev.map((row) =>
          row.id === n.id
            ? { ...row, status: 'SENT', read_at: null }
            : row,
        ),
      );
      setTotalUnread((n2) => n2 + 1);
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete || deleting) return;
    setDeleting(true);
    const target = confirmDelete;
    try {
      await deleteNotification(role, target.id);
      setItems((prev) => prev.filter((row) => row.id !== target.id));
      setTotal((t) => Math.max(0, t - 1));
      if (target.unread) setTotalUnread((n) => Math.max(0, n - 1));
      if (expandedId === target.id) setExpandedId(null);
      refresh();
      setConfirmDelete(null);
      // If this was the last row on the page, step back one page so
      // the user doesn't stare at an empty list with a page count.
      if (items.length === 1 && page > 1) setPage((p) => p - 1);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ??
          err?.message ??
          'Could not delete notification.',
      );
    } finally {
      setDeleting(false);
    }
  };

  // Runs only after the user confirms in the popup.
  const handleMarkAll = async () => {
    if (bulkBusy) return;
    if (totalUnread === 0) {
      setConfirmAllOpen(false);
      return;
    }
    setBulkBusy(true);
    const snapshot = items;
    const unreadSnapshot = totalUnread;
    setItems((prev) =>
      prev.map((row) =>
        row.read_at
          ? row
          : {
              ...row,
              status: 'READ',
              read_at: new Date().toISOString(),
            },
      ),
    );
    setTotalUnread(0);
    try {
      await markAllNotificationsRead(role);
      // Sync this page's badge; the bell updates via the broadcast event.
      refresh();
      setConfirmAllOpen(false);
    } catch {
      setItems(snapshot);
      setTotalUnread(unreadSnapshot);
    } finally {
      setBulkBusy(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="w-full max-w-[720px] mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-['Playfair_Display',sans-serif] font-extrabold text-[#8e2a2f] text-[24px] md:text-[28px] leading-tight">
            Notifications
          </h1>
          <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[13px] mt-1">
            {total === 0
              ? "You're all caught up."
              : `${total} total · ${totalUnread} unread`}
          </p>
        </div>
        {totalUnread > 0 ? (
          <button
            type="button"
            onClick={() => setConfirmAllOpen(true)}
            disabled={bulkBusy}
            className="text-[#8e2a2f] text-[13px] font-bold hover:underline disabled:opacity-50"
          >
            {bulkBusy ? 'Marking…' : 'Mark all as read'}
          </button>
        ) : null}
      </div>

      {loading ? (
        <p className="text-center text-[#908e8e] text-[14px] py-12">
          Loading your notifications…
        </p>
      ) : items.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-[#eee] rounded-2xl">
          <span className="material-icons text-[#ccc] text-[40px] block mb-2">
            notifications_none
          </span>
          <p className="font-['DM_Sans',sans-serif] text-[#3a3335] text-[14px] font-bold">
            No notifications yet
          </p>
          <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px]">
            We'll let you know here when something new comes in.
          </p>
        </div>
      ) : (
        <ul className="flex flex-col gap-3">
          {items.map((n) => {
            const unread = !n.read_at;
            const expanded = expandedId === n.id;
            const title = n.notification?.title ?? '';
            const body = n.notification?.body ?? '';
            return (
              <li key={n.id}>
                <div
                  className={`relative border rounded-xl transition-colors ${
                    unread
                      ? 'bg-[#fcfaf7] border-[#ede8e9] hover:border-[#8e2a2f]'
                      : 'bg-white border-[#eee] hover:border-[#ccc]'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => handleRowClick(n)}
                    disabled={busyId === n.id}
                    className="w-full text-left px-4 py-3 pr-12"
                  >
                    <div className="flex items-start gap-3">
                      <span
                        aria-hidden
                        className={`mt-[6px] inline-block w-[8px] h-[8px] rounded-full shrink-0 ${
                          unread ? 'bg-[#8e2a2f]' : 'bg-transparent border border-[#eee]'
                        }`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3">
                          <p
                            className={`font-['DM_Sans',sans-serif] text-[14px] ${
                              unread
                                ? 'font-bold text-[#3a3335]'
                                : 'font-bold text-[#3a3335]/80'
                            }`}
                          >
                            {title}
                          </p>
                          <span className="text-[#908e8e] text-[11px] whitespace-nowrap">
                            {formatWhen(n.sent_at ?? n.notification?.sent_at)}
                          </span>
                        </div>
                        <p
                          className={`mt-1 text-[#908e8e] text-[13px] ${
                            expanded ? '' : 'line-clamp-2'
                          }`}
                        >
                          {body}
                        </p>
                      </div>
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setConfirmDelete({ id: n.id, unread });
                    }}
                    aria-label="Delete notification"
                    title="Delete"
                    className="absolute right-2 top-2 w-8 h-8 flex items-center justify-center rounded-full text-[#908e8e] hover:text-[#8e2a2f] hover:bg-[#fbeaea] transition-colors"
                  >
                    <span className="material-icons text-[18px]">delete_outline</span>
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {!loading && totalPages > 1 ? (
        <div className="flex justify-center mt-6">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </div>
      ) : null}

      <ConfirmModal
        isOpen={confirmAllOpen}
        onClose={() => setConfirmAllOpen(false)}
        onConfirm={handleMarkAll}
        confirming={bulkBusy}
        title="Mark all as read?"
        message={`This will mark ${totalUnread} unread notification${
          totalUnread === 1 ? '' : 's'
        } as read.`}
        confirmLabel="Mark all as read"
        cancelLabel="Cancel"
      />

      <ConfirmModal
        isOpen={confirmDelete !== null}
        onClose={() => (deleting ? undefined : setConfirmDelete(null))}
        onConfirm={handleDelete}
        confirming={deleting}
        title="Delete notification?"
        message="This will permanently remove the notification from your inbox. This can't be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
      />
    </div>
  );
}
